import { ChevronDown, ChevronRight } from 'lucide-react'
import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from '@/components/ui/sidebar'
import { useTranslation } from '@/hooks/use-translation'
import { isValidTranslationKey } from '@/lib/i18n/translation-utils'
import { collectNavPaths, getNavItemPath, pathIsActive } from '@/lib/routing/nav-path'
import type { DynamicNavItem, NavItem } from '@/lib/routing/routing.types'
import { cn } from '@/lib/utils'
import { AppSidebarNavItem } from './AppSidebarItem'
import { Avatar } from './Avatar'

interface AppSidebarMenuProps {
  navItems: (NavItem | DynamicNavItem)[]
  isSubMenu?: boolean
  children?: React.ReactNode
}

function getActiveParentKeys(
  items: (NavItem | DynamicNavItem)[],
  pathname: string,
): Set<string> {
  const keys = new Set<string>()

  for (const item of items) {
    if (item.subItems && item.subItems.length > 0) {
      const hasActiveSubItem = item.subItems.some((subItem) =>
        pathIsActive(pathname, getNavItemPath(subItem.to)),
      )

      if (hasActiveSubItem) {
        keys.add(`${item.labelKey}-${item.to || 'parent'}`)
      }

      for (const key of getActiveParentKeys(item.subItems, pathname)) {
        keys.add(key)
      }
    }
  }

  return keys
}

export function AppSidebarMenu({
  navItems,
  isSubMenu = false,
  children,
}: AppSidebarMenuProps) {
  const t = useTranslation()
  const { pathname } = useLocation()
  const { state } = useSidebar()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set())
  const prevPathnameRef = useRef<string | null>(null)
  const prevNavItemsRef = useRef<(NavItem | DynamicNavItem)[] | null>(null)

  if (prevPathnameRef.current !== pathname || prevNavItemsRef.current !== navItems) {
    prevPathnameRef.current = pathname
    prevNavItemsRef.current = navItems
    const activeKeys = getActiveParentKeys(navItems, pathname)
    if (activeKeys.size > 0) {
      setExpandedItems((prev) => new Set([...prev, ...activeKeys]))
    }
  }

  const Wrapper = isSubMenu ? SidebarMenuSub : SidebarMenu

  const toggleItemExpansion = (itemKey: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey)
      } else {
        newSet.add(itemKey)
      }
      return newSet
    })
  }

  const filteredNavItems =
    state === 'collapsed'
      ? navItems.filter(
          (item) =>
            item.to !== undefined ||
            item.subItems?.some((subItem) => subItem.to !== undefined),
        )
      : navItems

  const siblingPaths = collectNavPaths(filteredNavItems)

  /**
   * Determines whether a candidate path is the most specific sibling match for
   * the current pathname, preventing ancestor routes from staying highlighted
   * when a longer sibling route is active.
   */
  const isMostSpecificMatch = (candidate: string) => {
    if (!pathIsActive(pathname, candidate)) {
      return false
    }

    return !siblingPaths.some(
      (sibling) =>
        sibling !== candidate &&
        sibling.length > candidate.length &&
        pathIsActive(pathname, sibling),
    )
  }

  return (
    <Wrapper className="gap-px">
      {filteredNavItems.map(({ to, labelKey, icon: Icon, avatar, subItems, badge }) => {
        const hasSubItems = subItems && subItems.length > 0
        const itemKey = `${labelKey}-${to || 'parent'}`
        const isExpanded = expandedItems.has(itemKey)
        const isParentItem = !to && hasSubItems
        const fallbackTo =
          state === 'collapsed' && isParentItem
            ? subItems.find((subItem) => subItem.to)?.to
            : undefined
        const resolvedTo = to ?? fallbackTo
        const hasActiveSubItem = hasSubItems
          ? subItems.some((subItem) => {
              const subPath = getNavItemPath(subItem.to)
              return subPath ? pathIsActive(pathname, subPath) : false
            })
          : false
        const active =
          (resolvedTo !== undefined && isMostSpecificMatch(getNavItemPath(resolvedTo))) ||
          (isParentItem && hasActiveSubItem)

        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton asChild isActive={active}>
              <AppSidebarNavItem
                to={resolvedTo}
                className={cn(
                  'flex items-center gap-2 text-sidebar-foreground',
                  active && 'text-sidebar-accent-foreground',
                )}
                onClick={
                  isParentItem && state !== 'collapsed'
                    ? () => toggleItemExpansion(itemKey)
                    : undefined
                }
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex min-w-0 flex-1 items-center gap-3 font-medium">
                    {avatar ? (
                      <Avatar
                        imageSrc={avatar.imageSrc}
                        name={avatar.name}
                        size={avatar.size || 5}
                      />
                    ) : (
                      Icon && (
                        <Icon
                          className={cn(
                            'h-[18px] w-[18px] text-sidebar-foreground',
                            active ? 'opacity-100' : 'opacity-[0.62]',
                          )}
                        />
                      )
                    )}
                    {state !== 'collapsed' && (
                      <>
                        <span className="text-sidebar-foreground leading-tight">
                          {isValidTranslationKey(labelKey) ? t(labelKey) : labelKey}
                        </span>
                        {badge && (
                          <span className="shrink-0 rounded-full bg-white/[0.18] px-[7px] py-px text-[11px] font-semibold leading-[1.5] text-white">
                            {badge}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {state !== 'collapsed' &&
                    isParentItem &&
                    (isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-sidebar-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-sidebar-foreground" />
                    ))}
                </div>
              </AppSidebarNavItem>
            </SidebarMenuButton>

            {isParentItem && isExpanded && state !== 'collapsed' && (
              <AppSidebarMenu navItems={subItems} isSubMenu={true} />
            )}
          </SidebarMenuItem>
        )
      })}
      {!isSubMenu && children}
    </Wrapper>
  )
}
